import { Typography } from "@mui/material"
import type { BiomeshOperationResponse } from "@phc/common/gen/threatAnalyticsGen"
import {
  getListOperationsBiomeshOperationListGetQueryKey,
  useDeleteOperationBiomeshOperationOperationIdDelete,
} from "@phc/common/gen/threatAnalyticsGen"
import { useQueryClient } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import type { FC } from "react"
import { useCallback } from "react"
import { Modal } from "../Shared/Modal"
import { extractApiError } from "./copUtils"

interface DeleteOperationDialogProps {
  open: boolean
  onClose: () => void
  operation: BiomeshOperationResponse
}

export const DeleteOperationDialog: FC<DeleteOperationDialogProps> = ({
  open,
  onClose,
  operation,
}) => {
  const queryClient = useQueryClient()
  const { mutateAsync: deleteOperation, isPending } =
    useDeleteOperationBiomeshOperationOperationIdDelete()

  const handleConfirm = useCallback(async () => {
    try {
      await deleteOperation({ operationId: operation.operation_id })
      await queryClient.invalidateQueries({
        queryKey: getListOperationsBiomeshOperationListGetQueryKey(),
      })
      enqueueSnackbar("Operation deleted", { variant: "success" })
      onClose()
    } catch (err) {
      enqueueSnackbar(extractApiError(err, "Failed to delete operation"), {
        variant: "error",
      })
    }
  }, [deleteOperation, operation.operation_id, queryClient, onClose])

  return (
    <Modal
      open={open}
      onClose={onClose}
      onConfirm={() => void handleConfirm()}
      title="Delete Operation"
      confirmLabel="Delete"
      disableBtns={isPending}
    >
      <Typography sx={{ mt: 1 }}>
        Are you sure you want to delete <strong>{operation.name}</strong>?
      </Typography>
    </Modal>
  )
}
